import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { apiRequest } from "@client/api/client";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";

export const Route = createFileRoute("/recovery")({
  component: RecoveryPage,
});

function RecoveryPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() && !fullName.trim()) {
      toast.error("Please provide either your email or full name.");
      return;
    }
    setLoading(true);
    try {
      await apiRequest("/api/public/recovery-requests", {
        method: "POST",
        public: true,
        body: { email, fullName },
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to submit request.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-primary px-4 py-12">
      <div className="w-full max-w-md rounded-lg bg-card p-8 shadow-lg">
        <div className="flex flex-col items-center justify-center space-y-2">
          <h1 className="hall-heading text-2xl font-bold">
            Credential Recovery
          </h1>
        </div>
        <p className="mt-1 text-sm text-center text-muted-foreground">
          If you have forgotten your password, enter your email or full name
          below. An Administrator will review your request and provide you with
          a temporary password.
        </p>

        {submitted ? (
          <div className="mt-6 text-center space-y-4">
            <p className="text-sm font-medium text-green-600 dark:text-green-400">
              Your request has been received!
            </p>
            <p className="text-sm text-muted-foreground">
              Please contact your Administrator through the organization's
              approved channel to retrieve your new temporary password once they
              have processed this request.
            </p>
            <Button
              className="mt-4"
              variant="outline"
              onClick={() => navigate({ to: "/auth" })}
            >
              Return to Sign In
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Optional if name is provided"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Optional if email is provided"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Submitting…" : "Request Assistance"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              disabled={loading}
              onClick={() => navigate({ to: "/auth" })}
            >
              Cancel
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
